import React, { useState, useEffect, useRef } from "react";
import { useCreateRegistrationMutation } from "../../store/api/scholineApi.js";
import { motion } from "framer-motion";
import { IoIosArrowRoundForward } from "react-icons/io";
import bg from "../../assets/bg.png";
import HeroPng from "../../assets/hero.png";
import { FaCheckCircle } from "react-icons/fa";
import Logo from "../../assets/Logo.png";
import { trackEvent, trackCustomEvent } from "../../pixel.js";

const RegisterPage = () => {
  const [firstName, setFirstname] = useState("");
  const [lastName, setLastname] = useState("");
  const [school, setSchool] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isRegistered, setIsRegistered] = useState(false);

  const [createRegistration, { data, error, isLoading }] =
    useCreateRegistrationMutation();

  // guard to avoid duplicate firing on re-renders
  const completionFiredRef = useRef(false);

  const handleSubmit = (event) => {
    event.preventDefault();
    setErrorMessage("");
    createRegistration({ firstName, lastName, school, phone, email });
  };

  useEffect(() => {
    if (data) {
      setIsRegistered(true);

      if (!completionFiredRef.current) {
        completionFiredRef.current = true;

        // Pixel standard & custom events
        trackEvent("CompleteRegistration");
        trackCustomEvent("RegisteredForm", {
          school,
          source: "register_page",
        });
      }
    }
    if (error) {
      console.log("Registration failed with error:", error);
      setErrorMessage(
        error?.data?.message || "L'inscription a échoué. Veuillez réessayer."
      );
    }
  }, [data, error, school]);

  return (
    <motion.div
      className="flex justify-center items-center min-h-screen bg-cover bg-center bg-no-repeat px-6 pt-24"
      style={{ backgroundImage: `url(${bg})` }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1 }}
    >
      <div className="container mx-auto grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
        {/* Left Side - Image */}
        <motion.div
          className="flex justify-center items-center"
          initial={{ x: -100 }}
          animate={{ x: 0 }}
          transition={{ duration: 1 }}
        >
          <motion.img
            src={HeroPng}
            alt="Hero"
            className="w-72 sm:w-80 md:w-96 lg:w-[460px] drop-shadow-2xl"
          />
        </motion.div>

        {/* Right Side - Form or Success Message */}
        {!isRegistered ? (
          <motion.div
            className="flex justify-center items-center w-full"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <div className="w-full max-w-md bg-white/30 backdrop-blur-xl border border-white/40 shadow-2xl rounded-2xl p-6 sm:p-8">
              <h2 className="text-center text-2xl font-bold text-primary mb-2">
                Créez votre compte
              </h2>
              <p className="text-center text-gray-700 text-sm mb-3">
                Inscrivez-vous pour rejoindre la plateforme{" "}
                <span className="font-semibold text-secondary">Scholine</span>
              </p>

              {errorMessage && (
                <p className="text-red-500 text-sm text-center mb-2">
                  {errorMessage}
                </p>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                {[
                  {
                    label: "Nom",
                    value: lastName,
                    setter: setLastname,
                    placeholder: "Entrez votre nom",
                  },
                  {
                    label: "Prénom",
                    value: firstName,
                    setter: setFirstname,
                    placeholder: "Entrez votre prénom",
                  },
                  {
                    label: "École",
                    value: school,
                    setter: setSchool,
                    placeholder: "Entrez le nom de votre école",
                  },
                  {
                    label: "Téléphone",
                    value: phone,
                    setter: setPhone,
                    placeholder: "Entrez votre numéro de téléphone",
                  },
                  {
                    label: "E-mail",
                    value: email,
                    setter: setEmail,
                    placeholder: "Entrez votre adresse e-mail",
                    type: "email",
                  },
                ].map((field, index) => (
                  <div key={index}>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      {field.label}
                    </label>
                    <input
                      type={field.type || "text"}
                      value={field.value}
                      onChange={(e) => field.setter(e.target.value)}
                      placeholder={field.placeholder}
                      className="w-full px-3 py-2 rounded-lg bg-white/70 border border-gray-300 focus:border-secondary focus:ring-2 focus:ring-secondary outline-none transition text-sm"
                      required
                    />
                  </div>
                ))}

                <div className="mt-6">
                  <button
                    type="submit"
                    className={`w-full flex justify-center items-center gap-2 py-2 text-white font-semibold rounded-xl shadow-lg transition-all duration-300 ${
                      isLoading
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-gradient-to-r from-primary to-secondary hover:shadow-2xl hover:scale-[1.02]"
                    }`}
                    disabled={isLoading}
                  >
                    {isLoading
                      ? "Inscription en cours..."
                      : "S'inscrire maintenant"}
                    {!isLoading && (
                      <IoIosArrowRoundForward className="text-2xl group-hover:translate-x-1 duration-300" />
                    )}
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        ) : (
          <motion.div
            className="flex justify-center items-center w-full"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1 }}
          >
            <div className="w-full max-w-md bg-white/40 backdrop-blur-xl border border-white/30 shadow-xl rounded-2xl p-8 text-center">
              <FaCheckCircle className="text-green-500 text-5xl mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-primary mb-2">
                Votre inscription est réussie 🎉
              </h2>
              <p className="text-gray-700 mb-6">
                Merci pour votre confiance ! Veuillez attendre notre réponse.
              </p>
              <a
                href="https://docsend.com/view/4j3vga5gj5dyamdd"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gradient-to-r from-secondary to-primary text-white font-bold py-2 px-4 rounded-lg inline-flex items-center gap-2 hover:shadow-xl hover:scale-[1.03] transition"
              >
                Aller à la présentation
                <IoIosArrowRoundForward className="text-xl" />
              </a>
            </div>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};

export default RegisterPage;
